import { useState, useEffect, useContext, useRef } from 'react'
import { useSelector } from 'react-redux'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { ModalContext } from '../ModalContext'
import { ChevronUpIcon, ChevronDownIcon, CircleStackIcon, ChartBarIcon } from '@heroicons/react/24/solid'
import { ConvertPoundsToNearestFiveKilograms } from 'functions/measurementSystemUtils'

import LinearDial from 'components/Dials/LinearDial'
import WorkoutSummaryExerciseSetListItem from 'components/WorkoutSummary/WorkoutSummaryExerciseSetListItem'

export default function WorkoutSummaryModal() {

    const { modalData } = useContext(ModalContext)

    const athleteId = useSelector(state => state.athleteProfile.userId)

    const hasFetchedRef = useRef(false)
    const [processedWorkoutTemplate, setProcessedWorkoutTemplate] = useState(null)
    const [totalSetsCompleted, setTotalSetsCompleted] = useState(0)
    const [percentageOfSetsCompleted, setPercentageOfSetsCompleted] = useState(0)
    const [totalVolumeLifted, setTotalVolumeLifted] = useState(0)
    const [totalRepsCompleted, setTotalRepsCompleted] = useState(0)
    const [expandPhaseJSON, setExpandPhaseJSON] = useState(null)

    const measurementSystem = 'imperial' // TODO: Load from backend?

    const FetchAndProcessWorkoutTemplateData = async () => {

        const jsonBody = {
            userId: athleteId,
            weekOfTheYearKey: modalData.weekOfTheYearKey,
            workoutTemplateKey: modalData.workoutTemplateKey,
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('PROGRAM', 'GET', '/completed_workout_data', jsonBody)

        // console.log(result.data.data)

        var temp_workoutTemplate = JSON.parse(JSON.stringify(result.data.data.workoutData))

        // Add init data if the workout was never completed
        // When starting a workout, additional data is added to the structure by the athlete app
        // If the workout is incomplete, that setup is never done, so it needs to be done here
        if (modalData.completionData.completed === false) {            
            temp_workoutTemplate.phases.map((phase) => {
                phase.exercises.map((exercise) => {
                    exercise.sets.map((set) => {
                        // Add default values for recommendation data
                        set.recommendationData = {
                            weightRecommendationLoaded: false,
                            weightRecommendation: 0,
                            weightRecommendationRounded: 0,
                            labelWeightRecommendationRounded: 0,
                            formulaTag: '',
                            equipmentDetails: null,
                        }
    
                        // Add default values for completed set data
                        set.completedSetData = {
                            savingResult: false,
                            completed: false,
                            completedTimestamp: null,
                            skipped: false,
                            setUnitsCompleted: 0,
                            movementDifficulty: 0,
                            difficulty: 0,
                            override: 0,
                            databaseId: 0,
                        }
                    })
                })
            })
        }

        // Calculate Stats
        setTotalSetsCompleted(CalculateTotalSetsCompleted(temp_workoutTemplate.phases))
        setPercentageOfSetsCompleted(CalculatePercentageOfSetsCompleted(temp_workoutTemplate.phases))

        // Set total volume completed
        var totalVolumeCompleted = modalData.completionData.totalVolumeCompleted
        if (measurementSystem === 'metric') totalVolumeCompleted = ConvertPoundsToNearestFiveKilograms(totalVolumeCompleted)
        setTotalVolumeLifted(totalVolumeCompleted)

        // Set total reps completed
        setTotalRepsCompleted(modalData.completionData.totalRepsCompleted)

        var temp_expandPhaseJSON = {}

        // Create JSON object for controlling the expand/collapse of each workout phase
        temp_workoutTemplate.phases.map((phase) => {
            temp_expandPhaseJSON[phase.phaseKey] = false
        })

        // console.log(temp_expandPhaseJSON)
        // console.log(temp_workoutTemplate)

        setExpandPhaseJSON(temp_expandPhaseJSON)

        setProcessedWorkoutTemplate(temp_workoutTemplate)
    }

    const CalculateTotalSetsCompleted = (phases) => {
        var totalSetsCompleted = 0

        phases.map((phase) => {
            phase.exercises.map((exercise) => {
                exercise.sets.map((set) => {
                    if (set.completedSetData.completed) totalSetsCompleted = totalSetsCompleted + 1
                })
            })
        })

        return totalSetsCompleted
    }

    const CalculatePercentageOfSetsCompleted = (phases) => {
        var totalSets = 0
        var totalSetsCompleted = 0

        phases.map((phase) => {
            phase.exercises.map((exercise) => {
                exercise.sets.map((set) => {
                    totalSets = totalSets + 1

                    if (set.completedSetData.completed) totalSetsCompleted = totalSetsCompleted + 1
                })
            })
        })

        return Math.round((totalSetsCompleted / totalSets) * 100)
    }

    const OnTogglePhaseExpand = (phaseKey) => {
        var temp_expandPhaseJSON = JSON.parse(JSON.stringify(expandPhaseJSON))
        temp_expandPhaseJSON[phaseKey] = !temp_expandPhaseJSON[phaseKey]
        setExpandPhaseJSON(temp_expandPhaseJSON)
    }

    useEffect(() => {
        if (hasFetchedRef.current) return
        hasFetchedRef.current = true
        FetchAndProcessWorkoutTemplateData()
    }, [modalData])

    if (processedWorkoutTemplate === null) {
        return (
            <div className='flex flex-col h-[75vh] overflow-y-auto'>
                <div className='space-y-4 p-4'>
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="text-center text-lg font-semibold leading-6 text-gray-900">
                {processedWorkoutTemplate.name}
            </div>

            {/* Sets Data */}
            <div className='space-y-2'>
                <h2 className="text-center">Total Sets Completed: {totalSetsCompleted}</h2>

                <LinearDial
                    fillPercentage={percentageOfSetsCompleted}
                    fillLabel={percentageOfSetsCompleted + '% Completed'}
                    emptyLabel={100 - percentageOfSetsCompleted + '% Discarded'}
                />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-x-4">
                
                {/* Total Volume Lifted */}
                <div className="p-4 bg-gray-100 rounded-lg">
                    <div className="flex items-center gap-x-4">
                        <div className="p-2 w-10 h-10 rounded-lg bg-orange-400">
                            <CircleStackIcon className="h-6 w-6 text-white" />
                        </div>

                        <div className="w-40 text-left">
                            <h3 className="font-bold">
                                {totalVolumeLifted} {measurementSystem === 'imperial' ? 'lbs' : 'kgs'}
                            </h3>
                            <p className="text-xs text-gray-600">Total Volume Lifted</p>
                        </div>
                    </div>
                </div>

                {/* Total Reps Completed */}
                <div className="p-4 bg-gray-100 rounded-lg">
                    <div className="flex items-center gap-x-4">
                        <div 
                            className="p-2 w-10 h-10 rounded-lg bg-orange-400"
                        >
                            <ChartBarIcon className="h-6 w-6 text-white" />
                        </div>

                        <div className="w-40 text-left">
                            <h3 className="font-bold">{totalRepsCompleted}</h3>
                            <p className="text-xs text-gray-600">Total Reps Completed</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loop over each phase */}
            {processedWorkoutTemplate.phases.map((phase) => (
                <div 
                    key={phase.phaseKey} 
                    className="rounded-xl overflow-hidden text-left"
                >

                    {/* Phase Header */}
                    <div 
                        className={`
                            p-4 flex items-center bg-gray-100 cursor-pointer  
                            ${expandPhaseJSON[phase.phaseKey] ? 'border-b border-gray-300' : ''}
                        `} 
                        onClick={() => OnTogglePhaseExpand(phase.phaseKey)}
                    >  
                        <h2 className="text-lg flex-grow">{phase.name}</h2>

                        {!expandPhaseJSON[phase.phaseKey] && <ChevronDownIcon className="h-5" />}
                        {expandPhaseJSON[phase.phaseKey] && <ChevronUpIcon className="h-5" />}
                    </div>

                    {/* {expandPhaseJSON[phase.phaseKey] && phase.type === 'circuit' && (
                        <>
                            {Array.from({ length: phase.circuitRounds }).map((_, setIndex) => (
                                <div key={setIndex}>
                                    <pre>{JSON.stringify(phase.exercises, null, 2)}</pre>
                                </div>
                            ))}
                        </>
                    )} */}

                    {/* CIRCUIT Phase Type */}
                    {expandPhaseJSON[phase.phaseKey] && phase.type === 'circuit' && (
                        <>
                            {Array.from({ length: phase.circuitRounds }).map((_, setIndex) => (
                                <div 
                                    key={setIndex} 
                                    className={`${setIndex < phase.circuitRounds - 1 ? 'border-b border-gray-300' : ''}`}
                                >
                                    {phase.exercises.map((exercise, exerciseIndex) => (
                                        <div 
                                            key={exerciseIndex} 
                                            className={`${exerciseIndex < phase.exercises.length - 1 ? 'border-b border-gray-300' : ''}`}
                                        >                                                
                                            <WorkoutSummaryExerciseSetListItem
                                                completed={exercise.sets[setIndex].completedSetData.completed}
                                                exerciseId={exercise.id}
                                                exerciseName={exercise.name}
                                                exerciseType={exercise.exerciseType}
                                                setIndex={setIndex}
                                                setsAssigned={phase.circuitRounds}
                                                setUnit={exercise.setUnit}
                                                repsAssigned={exercise.setUnit === 'reps' ? exercise.sets[setIndex].reps : exercise.sets[setIndex].seconds}
                                                setUnitsCompleted={exercise.sets[setIndex].completedSetData.setUnitsCompleted}
                                                weightRecommendation={exercise.sets[setIndex].recommendationData.weightRecommendation}
                                                weightRecommendationRounded={exercise.sets[setIndex].recommendationData.weightRecommendationRounded}
                                                movementDifficulty={exercise.sets[setIndex].completedSetData.movementDifficulty || 0}
                                                difficulty={exercise.sets[setIndex].completedSetData.difficulty}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </>
                    )}

                    {/* STRAIGHT Phase Type */}
                    {expandPhaseJSON[phase.phaseKey] && phase.type === 'straight' && (
                        <div>
                            {phase.exercises.map((exercise, exerciseIndex) => (
                                <div key={exerciseIndex} className={`${exerciseIndex < phase.exercises.length - 1 ? 'border-b border-gray-400' : ''}`}>
                                    {exercise.sets.map((set, setIndex) => {
                                        <WorkoutSummaryExerciseSetListItem
                                            completed={set.completedSetData.completed}
                                            exerciseId={exercise.id}
                                            exerciseName={exercise.name}
                                            exerciseType={exercise.exerciseType}
                                            setIndex={setIndex}
                                            setsAssigned={exercise.sets.length}
                                            setUnit={exercise.setUnit}
                                            repsAssigned={exercise.setUnit === 'reps' ? set.reps : set.seconds}
                                            setUnitsCompleted={set.completedSetData.setUnitsCompleted}
                                            weightRecommendation={set.recommendationData.weightRecommendation}
                                            weightRecommendationRounded={set.recommendationData.weightRecommendationRounded}
                                            movementDifficulty={set.completedSetData.movementDifficulty || 0}
                                            difficulty={set.completedSetData.difficulty}
                                        />
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                    
                </div>
            ))}
        </>
    )
}